import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

import { getUrl } from '../lib/serviceHelpers';

export const useFileUpload = (claimId, onSuccess) => {
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);

  // extraMeta (2026-09-28): merged into the file record's fileMeta, e.g.
  // { onboardingItem: 'dd214' } from the client's checklist upload.
  const startUpload = useCallback(async (filesOrFile, fileId = null, folder = null, extraMeta = null) => {
    // Normalize: accept a single File, a FileList, or an array
    const files = filesOrFile instanceof FileList
      ? Array.from(filesOrFile)
      : Array.isArray(filesOrFile)
        ? filesOrFile
        : [filesOrFile];

    setBusy(true);
    setTotal(files.length);
    let uploaded = 0;
    let failed = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setCurrent(i + 1);

      try {
        // 1. Get presigned URL
        const presignedRes = await axios.post(
          getUrl(`/claims/${claimId}/files/presigned-url`),
          {
            fileName: file.name,
            contentType: file.type,
            action: 'putObject',
            folder,
          },
        );
        const { url: presignedUrl, key: fileKey } = presignedRes.data;

        // 2. Upload to S3
        await axios({
          url: presignedUrl,
          method: 'PUT',
          data: file,
          headers: { 'Content-Type': file.type },
        });

        // 3. Create file record
        const payload = { fileName: file.name, fileLocation: fileKey };
        if (fileId) payload.fileId = fileId;
        if (extraMeta) payload.fileMeta = extraMeta;
        const fileData = (await axios.post(`/claims/${claimId}/files`, payload)).data;

        uploaded++;
        if (typeof onSuccess === 'function') {
          onSuccess(fileData);
        }
      } catch (err) {
        failed++;
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setBusy(false);
    setCurrent(0);
    setTotal(0);
    return { uploaded, failed };
  }, [claimId, onSuccess]);

  return { busy, current, total, startUpload };
};
